import { QueryClient, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { tenantApi } from '../lib/tenant-api';
import { isNil, Tenant } from '@activepieces/shared';
import { authenticationSession } from '@/lib/authentication-session';

export const tenantHooks = {
  useTenants: () => {
    return useQuery<Tenant[], Error>({
      queryKey: ['Tenant'],
      queryFn: async () => {
        const results = await tenantApi.list();
        return results;
      },
    });
  },
  useCurrentTenant: () => {
    const query = useSuspenseQuery<Tenant, Error>({
      queryKey: ['current-tenant'],
      queryFn: tenantApi.current,
      staleTime: Infinity,
    });
    return {
      ...query,
      tenant: query.data,
      setCurrentTenant,
    };
  },
  // Resolved locally, without any request. The route param only exists on the
  // admin flow viewer, where it is authoritative: a Kizeo operator may hold a
  // Khub session of their own, which is not the tenant being inspected.
  useTenantId: (): string =>
    useInspectedTenantId() ?? authenticationSession.tenantId ?? '',
  // Same param, read as a signal rather than as a value: its presence is the
  // only marker that the current screen is the admin flow viewer.
  useIsInspectingOtherTenant: (): boolean => !isNil(useInspectedTenantId()),
};

// Single reader of the route param: its name is known to this file alone.
const useInspectedTenantId = (): string | undefined => useParams().tenantId;

const setCurrentTenant = async (
  queryClient: QueryClient,
  tenant: Tenant,
) => {
  await authenticationSession.switchToTenant(tenant.id);
  queryClient.setQueryData(['current-tenant'], tenant);
};
